import type { AnalysisDimensionFilter } from './filterComparators';
import type { DashboardComponent } from '../services/dashboard.service';
import { DATE_RANGE_OPTIONS } from '../components/ui/date-range-picker';

const SCHEMA_VERSION = 1;

export interface PersistedDateRange {
  value: string;
  customStart: string;
  customEnd: string;
}

export interface PersistedDashboardFilters {
  v: number;
  dateRange: PersistedDateRange | null;
  dimensionFilters: AnalysisDimensionFilter[];
  // dims explicitly cleared by the user when the dashboard had a non-empty default
  clearedDimensionIds: string[];
}

export function buildPersistenceKey(
  tenantUuid: string | undefined,
  dashboardId: string | undefined,
): string | null {
  if (!tenantUuid || !dashboardId) return null;
  return `dashboard-filters:v${SCHEMA_VERSION}:${tenantUuid}:${dashboardId}`;
}

function isPersistedShape(value: unknown): value is PersistedDashboardFilters {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (v.v !== SCHEMA_VERSION) return false;
  if (v.dateRange !== null && typeof v.dateRange !== 'object') return false;
  if (!Array.isArray(v.dimensionFilters)) return false;
  // clearedDimensionIds is optional for forward-compat with older blobs
  if (v.clearedDimensionIds !== undefined && !Array.isArray(v.clearedDimensionIds)) return false;
  return true;
}

export function emptyPersisted(): PersistedDashboardFilters {
  return {
    v: SCHEMA_VERSION,
    dateRange: null,
    dimensionFilters: [],
    clearedDimensionIds: [],
  };
}

export function isPersistedEmpty(p: PersistedDashboardFilters): boolean {
  return (
    p.dateRange === null &&
    p.dimensionFilters.length === 0 &&
    p.clearedDimensionIds.length === 0
  );
}

export function readPersistedFromStorage(key: string | null): PersistedDashboardFilters {
  if (!key || typeof window === 'undefined') return emptyPersisted();
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return emptyPersisted();
    const parsed = JSON.parse(raw);
    if (!isPersistedShape(parsed)) {
      window.sessionStorage.removeItem(key);
      return emptyPersisted();
    }
    return {
      v: parsed.v,
      dateRange: parsed.dateRange,
      dimensionFilters: parsed.dimensionFilters,
      clearedDimensionIds: parsed.clearedDimensionIds || [],
    };
  } catch (error) {
    console.error('readPersistedFromStorage failed', key, error);
    return emptyPersisted();
  }
}

export function writePersistedToStorage(
  key: string | null,
  value: PersistedDashboardFilters,
): void {
  if (!key || typeof window === 'undefined') return;
  try {
    if (isPersistedEmpty(value)) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    }
  } catch (error) {
    console.error('writePersistedToStorage failed', key, error);
  }
}

interface DashboardLike {
  components?: DashboardComponent[];
}

function defaultedDimensionIds(dashboard: DashboardLike | null | undefined): Set<string> {
  const ids = new Set<string>();
  (dashboard?.components || []).forEach((c) => {
    if (c.object_type !== 'FILTER' || c.filter_config?.filter_type !== 'DIMENSION') return;
    const fc = c.filter_config;
    if (!fc.dimension_id || !fc.default_value) return;
    const values = Array.isArray(fc.default_value) ? fc.default_value : [fc.default_value];
    if (values.length > 0) ids.add(fc.dimension_id);
  });
  return ids;
}

export function reconcilePersistedFilters(
  raw: unknown,
  dashboard: DashboardLike | null | undefined,
  availableDateRangeValues: string[],
): PersistedDashboardFilters {
  if (!isPersistedShape(raw)) return emptyPersisted();

  const validDimensionIds = new Set<string>();
  (dashboard?.components || []).forEach((c) => {
    if (
      c.object_type === 'FILTER' &&
      c.filter_config?.filter_type === 'DIMENSION' &&
      c.filter_config?.dimension_id
    ) {
      validDimensionIds.add(c.filter_config.dimension_id);
    }
  });

  const dimensionFilters = raw.dimensionFilters.filter(
    (f) => f && typeof f === 'object' && validDimensionIds.has(f.dimension_id),
  );

  // Only keep cleared markers for dims that still have a non-empty default —
  // if the default went away or the filter was removed, "cleared" is meaningless.
  const stillDefaulted = defaultedDimensionIds(dashboard);
  const clearedDimensionIds = (raw.clearedDimensionIds || []).filter(
    (id) => typeof id === 'string' && stillDefaulted.has(id),
  );

  let dateRange: PersistedDateRange | null = null;
  if (raw.dateRange) {
    const dr = raw.dateRange;
    const allowedValues = new Set(
      availableDateRangeValues.length > 0
        ? availableDateRangeValues
        : DATE_RANGE_OPTIONS.map((o) => o.value),
    );
    if (typeof dr.value === 'string' && allowedValues.has(dr.value)) {
      dateRange = {
        value: dr.value,
        customStart: typeof dr.customStart === 'string' ? dr.customStart : '',
        customEnd: typeof dr.customEnd === 'string' ? dr.customEnd : '',
      };
    }
  }

  return { v: SCHEMA_VERSION, dateRange, dimensionFilters, clearedDimensionIds };
}

export interface DashboardDefaults {
  dateRange: string;
  customStart: string;
  customEnd: string;
  dimensionFilters: AnalysisDimensionFilter[];
}

export function diffFromDefaults(
  current: {
    dateRange: string;
    customStart: string;
    customEnd: string;
    dimensionFilters: AnalysisDimensionFilter[];
  },
  defaults: DashboardDefaults,
): PersistedDashboardFilters {
  const sameDateRange =
    current.dateRange === defaults.dateRange &&
    (current.dateRange !== 'custom' ||
      (current.customStart === defaults.customStart &&
        current.customEnd === defaults.customEnd));

  const dateRange: PersistedDateRange | null = sameDateRange
    ? null
    : {
        value: current.dateRange,
        customStart: current.customStart,
        customEnd: current.customEnd,
      };

  const normalize = (filters: AnalysisDimensionFilter[]) => {
    const m = new Map<string, string[]>();
    filters.forEach((f) => {
      const values = Array.isArray(f.value)
        ? f.value
        : f.value !== undefined && f.value !== ''
          ? [f.value]
          : [];
      m.set(f.dimension_id, [...values].sort());
    });
    return m;
  };

  const defMap = normalize(defaults.dimensionFilters);
  const currentMap = normalize(current.dimensionFilters);

  const dimensionFilters: AnalysisDimensionFilter[] = current.dimensionFilters.filter((f) => {
    const cur = currentMap.get(f.dimension_id) || [];
    const def = defMap.get(f.dimension_id) || [];
    return JSON.stringify(cur) !== JSON.stringify(def);
  });

  // Dims that have a non-empty default but are absent from current ⇒ user cleared them.
  const clearedDimensionIds: string[] = [];
  defMap.forEach((defValues, dimId) => {
    if (defValues.length > 0 && !currentMap.has(dimId)) {
      clearedDimensionIds.push(dimId);
    }
  });

  return { v: SCHEMA_VERSION, dateRange, dimensionFilters, clearedDimensionIds };
}

export function mergePersistedOverDefaults(
  persisted: PersistedDashboardFilters,
  defaults: DashboardDefaults,
): DashboardDefaults {
  const dateRange = persisted.dateRange?.value ?? defaults.dateRange;
  const customStart = persisted.dateRange?.customStart ?? defaults.customStart;
  const customEnd = persisted.dateRange?.customEnd ?? defaults.customEnd;

  const overrideMap = new Map<string, AnalysisDimensionFilter>();
  persisted.dimensionFilters.forEach((f) => overrideMap.set(f.dimension_id, f));
  const clearedSet = new Set(persisted.clearedDimensionIds);

  const merged: AnalysisDimensionFilter[] = [];
  const seen = new Set<string>();
  defaults.dimensionFilters.forEach((f) => {
    seen.add(f.dimension_id);
    if (clearedSet.has(f.dimension_id)) return; // user explicitly cleared this default
    merged.push(overrideMap.get(f.dimension_id) || f);
  });
  persisted.dimensionFilters.forEach((f) => {
    if (!seen.has(f.dimension_id)) merged.push(f);
  });

  return { dateRange, customStart, customEnd, dimensionFilters: merged };
}
